// utils/helpers.js

/** Uppercase first letter of each word; returns em dash for empty. */
export const titleCase = (input) => {
  const s = (input ?? "")
    .toString()
    .replace(/[_\-]+/g, " ")
    .trim();
  if (!s) return "—";
  return s
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ""))
    .join(" ");
};

/** Safe JSON.parse; returns fallback on error. */
export const safeJsonParse = (value, fallback = null) => {
  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

/** Return an array for any input. null/undefined => [] */
export const ensureArray = (v) => {
  if (Array.isArray(v)) return v;
  if (v == null) return [];
  return [v];
};

/**
 * Parse array-like values from backend:
 * - JSON strings: '["a","b"]'
 * - CSV strings:  'a,b , c'
 * - Already arrays: ['a','b']
 * - Numbers/booleans get stringified
 * Options:
 *  - split: delimiter for CSV (default ',')
 *  - trim: trim items (default true)
 *  - unique: dedupe (default true)
 *  - map: transform each item (default identity)
 */
export const parseArray = (input, opts = {}) => {
  const {
    split = ",",
    trim = true,
    unique = true,
    map = (x) => x,
  } = opts;

  if (input == null || input === "") return [];

  // Already an array
  if (Array.isArray(input)) {
    const arr = input.map((x) => (x == null ? "" : String(x)));
    return unique ? Array.from(new Set(arr.map(map))) : arr.map(map);
  }

  const str = String(input).trim();

  // Try JSON array first
  if (str.startsWith("[") && str.endsWith("]")) {
    const parsed = safeJsonParse(str, []);
    if (Array.isArray(parsed)) {
      const arr = parsed.map((x) => (x == null ? "" : String(x)));
      return unique ? Array.from(new Set(arr.map(map))) : arr.map(map);
    }
  }

  // Fallback to CSV
  const parts = str
    .split(split)
    .map((s) => (trim ? s.trim() : s))
    .filter((s) => s !== "");

  return unique ? Array.from(new Set(parts.map(map))) : parts.map(map);
};

/**
 * Normalize various truthy/falsy inputs into 'Y'/'N'.
 * - Accepts booleans, numbers, and strings like 'y','yes','true','1'
 * - When value is null/undefined:
 *    - if allowNull => returns null
 *    - else => returns 'N'
 */
export const toYN = (value, allowNull = false) => {
  if (value == null) return allowNull ? null : "N";
  const s = String(value).trim().toLowerCase();
  const truthy = ["y", "yes", "true", "1", "t"];
  const falsy = ["n", "no", "false", "0", "f"];
  if (truthy.includes(s)) return "Y";
  if (falsy.includes(s)) return "N";
  // fallback for non-string inputs like booleans/numbers
  return value ? "Y" : "N";
};

/** Convert 'Y'/'N'/null -> boolean/null */
export const fromYN = (value) => {
  if (value == null) return null;
  const s = String(value).trim().toUpperCase();
  if (s === "Y") return true;
  if (s === "N") return false;
  return null;
};

/** Number coercion that keeps nulls as null. */
export const toNumberId = (v) => (v == null || v === "" ? null : Number(v));

// Normalize country_id coming from inputs (string/number/"")
// -> number | null, safe for API payloads
export const normalizeCountryId = (v) => {
  if (v == null || v === "") return null;
  // Allow passing objects like { id: 123 } defensively
  const val = typeof v === "object" && v !== null && "id" in v ? v.id : v;
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
};

/**
 * Build a quick lookup map for countries by id and iso2.
 * @param {Array<{id:number|null, iso2:string, name:string}>} list
 * @returns {{byId: Map<number,object>, byIso: Map<string,object>, get:(idOrIso)=>object|null}}
 */
export const createCountryMap = (list = []) => {
  const byId = new Map();
  const byIso = new Map();

  for (const c of list) {
    if (!c) continue;
    if (c.id != null) byId.set(Number(c.id), c);
    if (c.iso2) byIso.set(String(c.iso2).toUpperCase(), c);
  }

  const get = (idOrIso) => {
    if (idOrIso == null) return null;
    if (typeof idOrIso === "number") return byId.get(idOrIso) || null;
    const iso = String(idOrIso).toUpperCase();
    return byIso.get(iso) || null;
  };

  return { byId, byIso, get };
};
